// src/screens/ConfirmEmailScreen.js
// The confirm-email page (STEP3_5A_TIERS_DESIGN.md 3.13.3, strings 5.17 J12
// and J12x; release R6): critcash.com/confirm-email?code=...&to=...[&ch=...]
//
// Firebase's "custom email action handler" pattern: the link in OUR
// confirmation email (sendEmailConfirmation, J11) lands here, never on
// Firebase's stock action page. The page applies the one-time code with
// applyActionCode (no sign-in needed: the code names the account), drops the
// code from the address bar, and shows:
//  - J12 "Your email is confirmed." plus "You can join channel memberships
//    now." only when memberships are live and the waiting period (call D)
//    is known not to apply to the account (the channel's offer has no
//    waitGate, or the account is older than the longest wait); while the
//    wait applies, J10's panel line follows instead. Signed out, dark, or
//    unknown: the first sentence alone (R6 copy read 12.3 note 1).
//    "Back to CritCast" goes to the allowlisted page: to=join with a valid
//    channel name -> that channel's page; anything else -> Verification
//    Center. The link never carries a URL.
//  - J12x when the code expired, was used or is unknown, unless the code
//    was already used (the link opened twice) and the signed-in account is
//    confirmed: then J12. An expired link never falls back (R6 review R6C-3).
//  - a generic failure with "Try again" for anything else (the network).
//
// The join panel and Verification Center re-check the account on their own
// (Auth reload, then the offer), so nothing else needs telling.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { applyActionCode } from 'firebase/auth';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';
import Footer from '../components/Footer';
import SpendingLimitNotice from '../components/SpendingLimitNotice';
import useMembershipsLive from '../hooks/useMembershipsLive';
import { getMembershipOffer } from '../utils/membershipPayment';
import { verifyLine } from '../utils/spendingLimit';
import {
  CONFIRM_PAGE_TITLE,
  CONFIRMING_LINE,
  CONFIRM_FAILED_LINE,
  LINK_USED_LINE,
  OPEN_VERIFICATION_LABEL,
  BACK_TO_CRITCAST_LABEL,
  TRY_AGAIN_LABEL,
  confirmedText,
  confirmEmailTarget,
  applyCodeErrorKind,
  usedCodeMayBeConfirmed,
  waitCannotApply,
  waitPanelLine,
  joinAtText,
  joinGatesOf,
} from '../utils/membershipJoin';

const COLORS = {
  BG: '#000',
  CARD: '#111',
  BORDER: '#222',
  GOLD: '#FCD000',
  GREEN: '#2ECC71',
  TEXT: '#EDEDED',
  DIM: '#aaa',
  RED: '#e05555',
};

// The signed-in account's Auth emailVerified after a reload (false when
// signed out or unreadable).
async function signedInConfirmed() {
  const u = auth.currentUser;
  if (!u) return false;
  try {
    await u.reload();
  } catch (_) {
    return false;
  }
  return auth.currentUser?.emailVerified === true;
}

// A public channel name -> its uid (public_profiles is queryable by
// userNameLower); null when not found.
async function channelUidOf(channel) {
  const snap = await getDocs(query(
    collection(db, 'public_profiles'),
    where('userNameLower', '==', String(channel).toLowerCase()),
    limit(1),
  ));
  return snap.empty ? null : snap.docs[0].id;
}

export default function ConfirmEmailScreen({ navigation, route }) {
  // Read once: the address bar drops the code after it is used.
  const linkRef = useRef(null);
  if (!linkRef.current) {
    const p = route?.params || {};
    linkRef.current = {
      code: typeof p.code === 'string' ? p.code.trim() : '',
      target: confirmEmailTarget({ to: p.to, ch: p.ch }),
    };
  }
  const { code, target } = linkRef.current;

  const flag = useMembershipsLive();
  // loading | confirmed | used | failed
  const [phase, setPhase] = useState('loading');
  const [canJoinNow, setCanJoinNow] = useState(false);
  const [waitLine, setWaitLine] = useState(null);
  const ranRef = useRef(false);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const run = useCallback(async () => {
    setPhase('loading');
    if (!code) {
      const confirmed = await signedInConfirmed();
      if (aliveRef.current) setPhase(confirmed ? 'confirmed' : 'used');
      return;
    }
    try {
      await applyActionCode(auth, code);
      // A used code is worth nothing, but a streamer's screen still should
      // not show one.
      try { navigation.setParams({ code: undefined }); } catch (_) { /* noop */ }
      await signedInConfirmed();
      if (aliveRef.current) setPhase('confirmed');
    } catch (err) {
      if (applyCodeErrorKind(err) === 'used') {
        // R6 review R6C-3: only an already-used code falls back to the
        // signed-in account (an expired link never confirmed anything).
        const confirmed = usedCodeMayBeConfirmed(err) ? await signedInConfirmed() : false;
        if (aliveRef.current) setPhase(confirmed ? 'confirmed' : 'used');
      } else {
        console.warn('[confirm-email] applyActionCode failed:', err?.code || err?.message);
        if (aliveRef.current) setPhase('failed');
      }
    }
  }, [code, navigation]);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    run();
  }, [run]);

  // J12's second sentence, or J10's panel line, once confirmed.
  useEffect(() => {
    if (phase !== 'confirmed' || flag.loading) return undefined;
    let alive = true;
    setCanJoinNow(false);
    setWaitLine(null);
    const u = auth.currentUser;
    if (!flag.live || !u) return undefined;
    (async () => {
      // The channel the join started from: its offer says whether a wait
      // applies to this account.
      if (target.route === 'StreamWatch') {
        try {
          const creatorUid = await channelUidOf(target.params.channel);
          if (creatorUid && creatorUid !== u.uid) {
            const o = await getMembershipOffer({ creatorUid });
            if (!alive) return;
            const g = joinGatesOf(o);
            if (g.wait) {
              setWaitLine(waitPanelLine({
                hours: g.wait.minAccountAgeHours,
                dateTime: joinAtText(g.wait.joinAtMs),
                verify: verifyLine(o?.limit?.verify),
              }));
              return;
            }
            if (o?.available === true && !g.email) {
              setCanJoinNow(true);
              return;
            }
          }
        } catch (err) {
          console.warn('[confirm-email] offer check failed:', err?.code || err?.message);
        }
      }
      // Otherwise only an account older than the longest wait is sure.
      if (alive) setCanJoinNow(waitCannotApply(Date.parse(u.metadata?.creationTime || '')));
    })();
    return () => { alive = false; };
  }, [phase, flag.live, flag.loading, target]);

  const goBack = () => navigation.navigate(target.route, target.params);
  const goVerification = () => navigation.navigate('Verification');

  const button = (label, onPress, kind = 'gold') => (
    <TouchableOpacity
      style={kind === 'gold' ? styles.goldBtn : styles.outlineBtn}
      onPress={onPress}
      accessibilityRole="button"
    >
      <Text style={kind === 'gold' ? styles.goldBtnText : styles.outlineBtnText}>{label}</Text>
    </TouchableOpacity>
  );

  const renderBody = () => {
    if (phase === 'loading') {
      return (
        <>
          <ActivityIndicator color={COLORS.GOLD} style={styles.spinner} />
          <Text style={styles.body}>{CONFIRMING_LINE}</Text>
        </>
      );
    }
    if (phase === 'confirmed') {
      return (
        <>
          <Text style={[styles.body, styles.ok]}>{confirmedText(canJoinNow && !waitLine)}</Text>
          {waitLine ? <SpendingLimitNotice line={waitLine} style={styles.waitBox} /> : null}
          {button(BACK_TO_CRITCAST_LABEL, goBack)}
        </>
      );
    }
    if (phase === 'used') {
      return (
        <>
          <Text style={styles.body}>{LINK_USED_LINE}</Text>
          {button(OPEN_VERIFICATION_LABEL, goVerification)}
        </>
      );
    }
    return (
      <>
        <Text style={[styles.body, styles.err]}>{CONFIRM_FAILED_LINE}</Text>
        {button(TRY_AGAIN_LABEL, run)}
        {button(OPEN_VERIFICATION_LABEL, goVerification, 'outline')}
      </>
    );
  };

  return (
    <View style={styles.outer}>
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.card}>
          <Text style={styles.title}>{CONFIRM_PAGE_TITLE}</Text>
          {renderBody()}
        </View>
      </ScrollView>
      <Footer navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  outer: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20 },
  card: {
    width: '100%', maxWidth: 440, backgroundColor: COLORS.CARD, borderColor: COLORS.BORDER,
    borderWidth: 1, borderRadius: 14, padding: 22,
  },
  title: { color: COLORS.GOLD, fontSize: 22, fontWeight: '800', textAlign: 'center', marginBottom: 12 },
  spinner: { paddingVertical: 12 },
  body: { color: COLORS.TEXT, fontSize: 14.5, lineHeight: 21, textAlign: 'center', marginTop: 4 },
  ok: { color: COLORS.GREEN, fontWeight: '700' },
  err: { color: COLORS.RED },
  waitBox: { marginTop: 12 },
  goldBtn: { backgroundColor: COLORS.GOLD, borderRadius: 10, paddingVertical: 12, alignItems: 'center', marginTop: 18 },
  goldBtnText: { color: '#0b0b0b', fontSize: 14, fontWeight: '800' },
  outlineBtn: {
    borderColor: COLORS.GOLD, borderWidth: 1, borderRadius: 10, paddingVertical: 11, alignItems: 'center', marginTop: 10,
  },
  outlineBtnText: { color: COLORS.GOLD, fontSize: 14, fontWeight: '800' },
});
